/**
 * stripCurrency
 *
 * Handles parsing and normalizing supported `amount` filter syntax. The
 * operator logic (>, >=, <, <=, =) is client-side only, as the BE supports only
 * exact match.
 * @param {string} raw - The raw amount value to strip.
 * @returns {string} - The value with currency symbols and commas removed.
 */
const stripCurrency = (raw) => String(raw).replace(/[$,]/g, "").trim();

const tryNumber = (raw) => {
  if (raw == null) return null;
  const cleaned = stripCurrency(raw);
  if (cleaned === "") return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? cleaned : null;
};

const GT_PHRASES = [
  { re: /^over\s+/i, strict: true },
  { re: /^above\s+/i, strict: true },
  { re: /^greater\s+than\s+/i, strict: true },
  { re: /^more\s+than\s+/i, strict: true },
  { re: /^at\s+least\s+/i, strict: false },
];
const LT_PHRASES = [
  { re: /^under\s+/i, strict: true },
  { re: /^below\s+/i, strict: true },
  { re: /^less\s+than\s+/i, strict: true },
  { re: /^at\s+most\s+/i, strict: false },
];

/**
 * parseAmountExpression
 *
 * Parses any of the supported amount expressions into a normalized shape and
 * returns null when the input isn't recognisable.
 * @param {string} raw - The raw amount expression to parse.
 * @returns {object|null} - The normalized expression, or null when
 * unrecognisable.
 */
const parseAmountExpression = (raw) => {
  if (raw == null) return null;
  let s = String(raw).trim();
  if (s === "") return null;

  // Rewrite word operators to symbolic so the rest of the parser is uniform.
  for (const { re, strict } of GT_PHRASES) {
    if (re.test(s)) {
      s = (strict ? ">" : ">=") + s.replace(re, "");
      break;
    }
  }
  for (const { re, strict } of LT_PHRASES) {
    if (re.test(s)) {
      s = (strict ? "<" : "<=") + s.replace(re, "");
      break;
    }
  }

  const rangeMatch = s.match(/^\$?\s*([\d.,]+)\s*(?:-|–|to)\s*\$?\s*([\d.,]+)$/i);
  if (rangeMatch) {
    const min = tryNumber(rangeMatch[1]);
    const max = tryNumber(rangeMatch[2]);
    if (min != null && max != null) return { kind: "range", min, max };
  }

  const opMatch = s.match(/^(>=|<=|>|<)\s*\$?\s*([\d.,]+)$/);
  if (opMatch) {
    const num = tryNumber(opMatch[2]);
    if (num != null) {
      const op = opMatch[1];
      return op.startsWith(">")
        ? { kind: "min", value: num, strict: op === ">" }
        : { kind: "max", value: num, strict: op === "<" };
    }
  }

  const bare = tryNumber(s);
  if (bare != null) return { kind: "exact", value: bare };

  return null;
};

const formatExpression = (expr) => {
  switch (expr?.kind) {
    case "exact":
      return expr.value;
    case "min":
      return `${expr.strict ? ">" : ">="} ${expr.value}`;
    case "max":
      return `${expr.strict ? "<" : "<="} ${expr.value}`;
    case "range":
      return `${expr.min} - ${expr.max}`;
    default:
      return "";
  }
};

// Translate endpoint may emit operator as "gt", ">", or English phrases. OPERATOR_ALIASES is used to normalize any reasonable spelling to gt/gte/lt/lte/eq, or null
const OPERATOR_ALIASES = {
  gt: "gt",
  ">": "gt",
  greater: "gt",
  greater_than: "gt",
  "greater than": "gt",
  more: "gt",
  more_than: "gt",
  "more than": "gt",
  over: "gt",
  above: "gt",
  gte: "gte",
  ">=": "gte",
  "≥": "gte",
  at_least: "gte",
  "at least": "gte",
  min: "gte",
  minimum: "gte",
  lt: "lt",
  "<": "lt",
  less: "lt",
  less_than: "lt",
  "less than": "lt",
  under: "lt",
  below: "lt",
  lte: "lte",
  "<=": "lte",
  "≤": "lte",
  at_most: "lte",
  "at most": "lte",
  max: "lte",
  maximum: "lte",
  eq: "eq",
  "=": "eq",
  "==": "eq",
  equals: "eq",
  equal: "eq",
};

const normalizeOperator = (raw) => {
  if (raw == null) return null;
  const key = String(raw).toLowerCase().trim().replace(/\s+/g, " ");
  return OPERATOR_ALIASES[key] || null;
};

/**
 * parseTranslatedAmount
 *
 * Normalizes the translate endpoint response into a canonical criteria-form
 * string and returns "" when nothing usable can be extracted; otherwise it
 * handles shapes we've observed, including primitive values, primitive values
 * where value has an operator (i.e { value: ">100" }), or where value is bare
 * but label has operator (i.e. { value: 100, label: "Over $100" }), and ranges
 * (i.e. { min: 100, max: 200 }).
 * @param {object|string|number} field - The translated amount field from the
 * endpoint.
 * @returns {string} - The canonical criteria-form string, or "" when nothing
 * usable can be extracted.
 */
export const parseTranslatedAmount = (field) => {
  if (field == null) return "";

  if (typeof field === "object" && !Array.isArray(field)) {
    if (field.min != null && field.max != null) {
      const min = tryNumber(field.min);
      const max = tryNumber(field.max);
      if (min != null && max != null)
        return formatExpression({ kind: "range", min, max });
    }
    const op = normalizeOperator(field.operator);
    if (op != null && field.value != null) {
      const num = tryNumber(field.value);
      if (num != null) {
        if (op === "gt")
          return formatExpression({ kind: "min", value: num, strict: true });
        if (op === "gte")
          return formatExpression({ kind: "min", value: num, strict: false });
        if (op === "lt")
          return formatExpression({ kind: "max", value: num, strict: true });
        if (op === "lte")
          return formatExpression({ kind: "max", value: num, strict: false });
        if (op === "eq") return formatExpression({ kind: "exact", value: num });
      }
    }
    if (field.value != null) {
      const parsedValue = parseAmountExpression(field.value);
      // If value is bare but label has operator, prefer the label to avoid silently degrading to exact match (root cause of the no-results bug).
      if (
        parsedValue?.kind === "exact" &&
        typeof field.label === "string" &&
        field.label.trim() !== ""
      ) {
        const parsedLabel = parseAmountExpression(field.label);
        if (parsedLabel && parsedLabel.kind !== "exact") {
          return formatExpression(parsedLabel);
        }
      }
      if (parsedValue) return formatExpression(parsedValue);
    }
    return "";
  }

  const parsed = parseAmountExpression(field);
  return parsed ? formatExpression(parsed) : "";
};

/**
 * toAmountQueryParams
 *
 * Decompose amount string into BE query parameters. The `amount` string
 * supports operators and ranges for NLS and falls back to bare `amount=` for
 * legacy search support. Legacy API only supports single exact-match `amount`
 * param, so range/operator expressions intentionally produce no params here.
 * @param {string} rawAmount - The raw amount string to decompose.
 * @returns {object} - The query parameters, or {} when no exact-match amount is
 * present.
 */
export const toAmountQueryParams = (rawAmount) => {
  const parsed = parseAmountExpression(rawAmount);
  if (!parsed || parsed.kind !== "exact") return {};
  return { amount: parsed.value };
};

/**
 * getAmountPredicate
 *
 * Returns null when the form value is exact-match or empty; otherwise the
 * predicate the epic uses to drop non-matching rows. Numeric bounds are floats
 * so comparisons work directly against parsed dollar amounts.
 * @param {string} rawAmount - The raw amount form value.
 * @returns {object|null} - The amount predicate, or null when exact-match or
 * empty.
 */
export const getAmountPredicate = (rawAmount) => {
  const parsed = parseAmountExpression(rawAmount);
  if (!parsed || parsed.kind === "exact") return null;
  switch (parsed.kind) {
    case "min":
      return { min: parseFloat(parsed.value), minStrict: parsed.strict };
    case "max":
      return { max: parseFloat(parsed.value), maxStrict: parsed.strict };
    case "range":
      // Ranges ("between X and Y", "X to Y") are inclusive on both ends
      return {
        min: parseFloat(parsed.min),
        max: parseFloat(parsed.max),
        minStrict: false,
        maxStrict: false,
      };
    default:
      return null;
  }
};

/**
 * inferOperatorFromQuery
 *
 * If the backend doesn't surface operators like "over $100", this recovers it
 * from the query and is left alone otherwise. Backwards compatible because the
 * input is unchanged if no matching operator phrase is paired with amount.
 * @param {string} formValue - The current amount form value.
 * @param {string} originalQuery - The original natural-language query.
 * @returns {string} - The form value with a recovered operator, or the
 * unchanged input.
 */
export const inferOperatorFromQuery = (formValue, originalQuery) => {
  if (!formValue || !originalQuery) return formValue;

  const parsed = parseAmountExpression(formValue);
  if (!parsed || parsed.kind !== "exact") return formValue;

  const numForRegex = parsed.value.replace(/\./g, "\\.");
  // Match amount in query with optional $. Lookahead (?!\d) prevents "100" from matching inside "1000".
  const amountInQuery = new RegExp(`\\$?\\s*${numForRegex}(?!\\d)`, "i");
  const match = originalQuery.match(amountInQuery);
  if (!match) return formValue;

  const before = originalQuery.slice(0, match.index).toLowerCase().trimEnd();

  // Check negated phrases ("no more than", "at most") first to avoid matching "more than" inside "no more than".
  if (/\b(at\s+least|minimum\s+of)$/.test(before)) {
    return `>= ${parsed.value}`;
  }
  if (/\b(at\s+most|no\s+more\s+than|maximum\s+of)$/.test(before)) {
    return `<= ${parsed.value}`;
  }
  if (/\b(over|above|more\s+than|greater\s+than|exceeding)$/.test(before)) {
    return `> ${parsed.value}`;
  }
  if (/\b(under|below|less\s+than|fewer\s+than)$/.test(before)) {
    return `< ${parsed.value}`;
  }

  return formValue;
};

export const amountMatchesPredicate = (amount, predicate) => {
  if (predicate == null) return true;
  if (typeof amount !== "number" || !Number.isFinite(amount)) return false;
  if (predicate.min != null) {
    const ok = predicate.minStrict
      ? amount > predicate.min
      : amount >= predicate.min;
    if (!ok) return false;
  }
  if (predicate.max != null) {
    const ok = predicate.maxStrict
      ? amount < predicate.max
      : amount <= predicate.max;
    if (!ok) return false;
  }
  return true;
};
